import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { SEO } from "@/components/SEO";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import InviteLinkModal from "@/components/InviteLinkModal";
import { 
  House, Users, ArrowsLeftRight, CalendarCheck, ChatCircle, 
  Gear, IdentificationCard, SignOut, Crown, ChartLineUp, CaretRight,
  List, X, Sun, Moon, Storefront, Buildings, Tag, MagnifyingGlass,
  Funnel, Plus, MapPin, Trophy, Phone, EnvelopeSimple, Bell,
  UserCirclePlus, Handshake, Calendar, Warning, Sparkle, Notebook, Heart, Medal, CheckCircle, LinkSimple, Certificate, UsersThree, UserPlus
} from "@phosphor-icons/react";

const LOGO_URL = "/bch-logo.png";

const NOTIF_ICONS = {
  approval: { icon: UserCirclePlus, gradient: "from-rose-400 to-pink-500" },
  referral: { icon: Handshake, gradient: "from-teal-400 to-emerald-500" },
  meeting: { icon: Calendar, gradient: "from-sky-400 to-blue-500" },
  membership: { icon: Warning, gradient: "from-amber-400 to-orange-500" },
  visitor_thanks: { icon: Heart, gradient: "from-pink-400 to-rose-500" },
  testimonial: { icon: Certificate, gradient: "from-amber-400 to-yellow-500" },
  store: { icon: Storefront, gradient: "from-violet-400 to-purple-500" },
};

const NotificationBell = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_new: false })));
    } catch (err) {
      console.error("Failed to mark notifications read:", err);
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/notifications/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to clear notifications:", err);
    }
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open && unreadCount > 0) markRead();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        data-testid="notification-bell"
        className="relative p-2 hover:bg-[var(--bg-card)] rounded-xl transition-colors"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? "text-bch-teal" : "text-secondary"}`} weight={unreadCount > 0 ? "fill" : "duotone"} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] shadow-sm" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-[var(--border-color)] rounded-2xl shadow-2xl z-[60] overflow-hidden" data-testid="notification-dropdown">
          <div className="p-4 border-b border-[var(--border-color)] flex items-center justify-between">
            <h3 className="text-primary font-semibold text-sm">Notifications</h3>
            {notifications.length > 0 && (
              <button onClick={clearAll} className="text-rose-500 text-xs font-medium hover:text-rose-600 transition-colors" data-testid="clear-notifications-btn">Clear all</button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((n) => {
                const cfg = NOTIF_ICONS[n.type] || NOTIF_ICONS.referral;
                const Icon = cfg.icon;
                return (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => setOpen(false)}
                    className={`flex items-start gap-3 p-4 hover:bg-surface transition-colors border-b border-[var(--border-color)] last:border-b-0 ${n.is_new ? "bg-bch-teal/5" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center shrink-0 mt-0.5`}>
                      <Icon className="w-4 h-4 text-white" weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${n.is_new ? "text-primary font-semibold" : "text-primary font-medium"}`}>{n.title}</p>
                      <p className="text-muted text-xs mt-0.5 truncate">{n.message}</p>
                    </div>
                    {n.is_new && <div className="w-2 h-2 rounded-full bg-bch-teal shrink-0 mt-2" />}
                  </Link>
                );
              })
            ) : (
              <div className="py-10 text-center">
                <Bell className="w-10 h-10 text-muted/20 mx-auto mb-2" weight="duotone" />
                <p className="text-muted text-sm">No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Persist sidebar scroll position across re-mounts
let _sidebarScrollY = 0;

import { LoadingImage } from "@/components/LoadingImage";

export const DashboardLayout = ({ children }) => {
  const { user, logout, isSuperAdmin, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarNavRef = useRef(null);
  const [profilePic, setProfilePic] = useState(null);

  useEffect(() => {
    if (user?.id && token) {
      axios.get(`${API}/public/profile/${user.id}`).then(res => {
        if (res.data?.profile_picture) setProfilePic(res.data.profile_picture);
      }).catch(() => {});
    }
  }, [user?.id, token]);
  
  // Restore sidebar scroll position after mount
  useEffect(() => {
    if (sidebarNavRef.current) {
      sidebarNavRef.current.scrollTop = _sidebarScrollY;
    }
  }, []);

  // Save sidebar scroll on every scroll event
  const handleSidebarScroll = useCallback((e) => {
    _sidebarScrollY = e.target.scrollTop;
  }, []);
  
  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: House },
    { path: "/members", label: "Members", icon: Users },
    { path: "/referrals", label: "Referrals", icon: ArrowsLeftRight },
    { path: "/meetings", label: "Meetings", icon: CalendarCheck },
    { path: "/chat", label: "Chat", icon: ChatCircle },
    { path: "/stores", label: "Stores", icon: Storefront },
    { path: "/id-card", label: "ID Card", icon: IdentificationCard },
    { path: "/ai-insights", label: "AI Insights", icon: Sparkle },
    { path: "/leaderboard", label: "Leaderboard", icon: Medal },
    { path: "/testimonials", label: "Testimonials", icon: Certificate },
  ];

  const adminItems = isSuperAdmin ? [
    { path: "/admin/users", label: "Users", icon: Crown },
    { path: "/admin/chapters", label: "Chapters", icon: Buildings },
    { path: "/admin/categories", label: "Categories", icon: Tag },
    { path: "/admin/visitors", label: "Visitors", icon: Notebook },
    { path: "/admin/leads", label: "Leads", icon: UserPlus },
    { path: "/admin/partner-stores", label: "Partner Stores", icon: Storefront },
  ] : [];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay md:hidden ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      {/* Sidebar */}
      <aside className={`sidebar-mobile md:sticky md:top-0 md:left-0 w-64 bg-surface border-r border-[var(--border-color)] flex flex-col fixed h-screen z-50 ${sidebarOpen ? 'open' : ''}`}>
        <div className="px-5 py-2 md:px-6 md:py-2 border-b border-[var(--border-color)] flex items-center justify-between h-16 md:h-[72px]">
          <img src={LOGO_URL} alt="BCH Logo" className="h-16 md:h-20 w-auto -my-1" loading="eager" decoding="sync" />
          <button 
            className="md:hidden p-2 hover:bg-[var(--bg-card)] rounded"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5 text-secondary" />
          </button>
        </div>
        
        <nav ref={sidebarNavRef} onScroll={handleSidebarScroll} className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? "active bg-bch-teal/15 text-bch-teal"
                    : "text-secondary hover:text-primary hover:bg-[var(--bg-card)]"
                }`}
              >
                <Icon className="w-5 h-5" weight={isActive(item.path) ? "fill" : "duotone"} />
                {item.label}
              </Link>
            );
          })}

          {adminItems.length > 0 && (
            <>
              <div className="pt-4 pb-2 px-4">
                <p className="text-muted text-[10px] uppercase tracking-widest font-semibold">Admin</p>
              </div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-admin-${item.label.toLowerCase()}`}
                    className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive(item.path)
                        ? "active bg-bch-gold/15 text-bch-gold"
                        : "text-secondary hover:text-primary hover:bg-[var(--bg-card)]"
                    }`}
                  >
                    <Icon className="w-5 h-5" weight={isActive(item.path) ? "fill" : "duotone"} />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-[var(--border-color)]">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-secondary hover:text-primary hover:bg-[var(--bg-card)] transition-colors text-sm mb-2"
            data-testid="sidebar-theme-toggle"
          >
            {theme === "dark" ? (
              <>
                <Sun className="w-5 h-5 text-amber-400" weight="fill" />
                Light Mode
              </>
            ) : (
              <>
                <Moon className="w-5 h-5 text-indigo-400" weight="fill" />
                Dark Mode
              </>
            )}
          </button>
          
          <Link
            to={`/p/${user?.id}`}
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--bg-card)] transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-bch-teal to-emerald-500 flex items-center justify-center overflow-hidden">
              {profilePic ? (
                <LoadingImage src={profilePic} className="w-10 h-10 rounded-xl" fallback={
                  <span className="text-white font-bold">{user?.full_name?.charAt(0)}</span>
                } />
              ) : (
                <span className="text-white font-bold">{user?.full_name?.charAt(0)}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-primary font-medium truncate text-sm">{user?.full_name}</p>
              <p className="text-muted text-xs capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
            <Gear className="w-4 h-4 text-muted" weight="duotone" />
          </Link>
          
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-4 py-3 mt-2 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors text-sm"
          >
            <SignOut className="w-5 h-5" weight="duotone" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-0 overflow-y-auto h-screen">
        {/* Mobile Header */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 glass p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-[var(--bg-card)] rounded"
            data-testid="mobile-menu-btn"
          >
            <List className="w-6 h-6 text-primary" weight="bold" />
          </button>
          <img src={LOGO_URL} alt="BCH" className="h-16 w-auto" />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={toggleTheme} className="p-2 hover:bg-[var(--bg-card)] rounded">
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-bch-gold" weight="duotone" />
              ) : (
                <Moon className="w-5 h-5 text-bch-teal" weight="duotone" />
              )}
            </button>
          </div>
        </div>

        {/* Desktop Top Bar */}
        <div className="hidden md:flex items-center justify-end gap-2 px-8 py-4 border-b border-[var(--border-color)]">
          <NotificationBell />
        </div>
        
        <div className="p-4 md:p-8 pt-20 md:pt-4">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
};

export default function DashboardPage() {
  const { user, token, isSuperAdmin } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Super Admin states
  const [dateFilter, setDateFilter] = useState(0);
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [chapterForm, setChapterForm] = useState({ name: "", city: "", state: "", description: "" });
  const [creatingChapter, setCreatingChapter] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [topInviters, setTopInviters] = useState([]);
  const [categoryData, setCategoryData] = useState(null);
  const [partnerStoreStats, setPartnerStoreStats] = useState(null);
  const [myVisitorCount, setMyVisitorCount] = useState({ total: 0, present: 0 });

  const fetchDashboard = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const promises = [
        axios.get(`${API}/analytics/dashboard`, { headers }),
        axios.get(`${API}/membership/status`, { headers }),
        axios.get(`${API}/analytics/top-inviters`, { headers }),
        axios.get(`${API}/categories/availability`, { headers }),
        axios.get(`${API}/my-visitors-count`, { headers })
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/analytics/admin`, { headers }));
        promises.push(axios.get(`${API}/admin/partner-store-analytics`, { headers }).catch(() => ({ data: null })));
      }
      const results = await Promise.all(promises);
      setDashboardData(results[0].data);
      setMembershipStatus(results[1].data);
      setTopInviters(results[2].data || []);
      setCategoryData(results[3].data);
      setMyVisitorCount(results[4].data || { total: 0, present: 0 });
      if (isSuperAdmin && results[5]) {
        setAdminData(results[5].data);
        if (results[6]?.data) setPartnerStoreStats(results[6].data.overview);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  usePolling(fetchDashboard, 10000);

  // Refetch admin data when date filter changes
  useEffect(() => {
    if (isSuperAdmin && !loading) {
      fetchAdminData(dateFilter);
    }
  }, [dateFilter, isSuperAdmin, loading]);

  const fetchAdminData = async (days) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const url = days > 0 ? `${API}/analytics/admin?days=${days}` : `${API}/analytics/admin`;
      const res = await axios.get(url, { headers });
      setAdminData(res.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    }
  };

  const handleCreateChapter = async (e) => {
    e.preventDefault();
    setCreatingChapter(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      await axios.post(`${API}/chapters`, {
        name: chapterForm.name,
        city: chapterForm.city,
        state: chapterForm.state,
        location: `${chapterForm.city}${chapterForm.state ? ", " + chapterForm.state : ""}`,
        description: chapterForm.description
      }, { headers });
      setShowAddChapter(false);
      setChapterForm({ name: "", city: "", state: "", description: "" });
      fetchAdminData(dateFilter);
    } catch (err) {
      console.error("Failed to create chapter:", err);
    } finally {
      setCreatingChapter(false);
    }
  };

  const fetchBreakdown = async (metric) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${API}/analytics/admin/breakdown?metric=${metric}${dateFilter > 0 ? `&days=${dateFilter}` : ""}`;
      const res = await axios.get(url, { headers });
      setBreakdownData(res.data);
    } catch (err) {
      console.error("Breakdown fetch failed:", err);
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
        </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <>
      <SEO title="Dashboard" description="Your BCH dashboard — track referrals, meetings, business growth, and chapter performance." path="/dashboard" noIndex />
      <div className="space-y-6 md:space-y-8 animate-fadeInUp">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-primary mb-2">
              Welcome, {user?.full_name?.split(" ")[0]}
            </h1>
            <p className="text-secondary text-sm md:text-base">
              {isSuperAdmin ? "Super Admin Dashboard" : (user?.chapter_name ? `${user.chapter_name} Chapter` : "Member Dashboard") + ` • ${user?.business_category}`}
            </p>
          </div>
          <Link 
            to="/referrals"
            data-testid="create-referral-btn"
            className="bg-bch-teal hover:bg-bch-teal-dark text-white px-5 md:px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all text-sm md:text-base shadow-lg shadow-bch-teal/20 hover:shadow-bch-teal/30 hover:-translate-y-0.5"
          >
            <ArrowsLeftRight className="w-5 h-5" weight="bold" />
            New Referral
          </Link>
        </div>

        {/* Stats Grid */}
        {isSuperAdmin && adminData ? (
          <>
            {/* Admin Toolbar: Date Filter + Add Chapter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end" data-testid="admin-toolbar">
              {/* Date Filter */}
              <div className="relative">
                <Funnel className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" weight="bold" />
                <select
                  data-testid="admin-date-filter"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(Number(e.target.value))}
                  className="pl-10 pr-8 py-2.5 bg-surface border border-[var(--border-color)] rounded-xl text-primary text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-bch-teal/30 focus:border-bch-teal transition-all min-w-[160px]"
                >
                  <option value={0}>All Time</option>
                  <option value={7}>Last 7 Days</option>
                  <option value={30}>Last 30 Days</option>
                  <option value={90}>Last 3 Months</option>
                  <option value={180}>Last 6 Months</option>
                  <option value={365}>Last 12 Months</option>
                </select>
              </div>

              {/* Add Chapter Button */}
              <button
                data-testid="add-chapter-btn"
                onClick={() => setShowAddChapter(true)}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-bch-teal to-emerald-500 text-white rounded-xl text-sm font-medium shadow-md shadow-bch-teal/20 hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                <Plus className="w-4 h-4" weight="bold" />
                Add Chapter
              </button>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
              <StatsCard
                label="Total Business"
                value={`₹${formatNumber(adminData.overview?.total_business_exchanged || 0)}`}
                icon={ChartLineUp}
                gradient="from-amber-400 to-orange-500"
                bgLight="bg-amber-50"
                darkClass="stat-card-amber"
                onClick={() => fetchBreakdown("total_business")}
              />
              <StatsCard
                label="Total Referrals"
                value={adminData.overview?.total_referrals || 0}
                icon={ArrowsLeftRight}
                gradient="from-teal-400 to-emerald-500"
                bgLight="bg-emerald-50"
                darkClass="stat-card-emerald"
                onClick={() => fetchBreakdown("total_referrals")}
              />
              <StatsCard
                label="Total Members"
                value={adminData.overview?.total_members || 0}
                icon={Users}
                gradient="from-violet-400 to-purple-500"
                bgLight="bg-violet-50"
                darkClass="stat-card-violet"
                onClick={() => fetchBreakdown("total_members")}
              />
              <StatsCard
                label="Active Chapters"
                value={adminData.overview?.total_chapters || 0}
                icon={Buildings}
                gradient="from-sky-400 to-blue-500"
                bgLight="bg-sky-50"
                darkClass="stat-card-sky"
                onClick={() => fetchBreakdown("active_chapters")}
              />
              <StatsCard
                label="Pending Approvals"
                value={adminData.overview?.pending_approvals || 0}
                icon={Crown}
                gradient="from-rose-400 to-pink-500"
                bgLight="bg-rose-50"
                darkClass="stat-card-rose"
                onClick={() => fetchBreakdown("pending_approvals")}
              />
            </div>

            {/* Visitors Brought - Admin View */}
            <div className="grid grid-cols-2 gap-3 md:gap-5">
              <StatsCard
                label="Visitors Invited"
                value={myVisitorCount.total}
                subtext={`${myVisitorCount.present} marked present`}
                icon={UsersThree}
                gradient="from-violet-400 to-purple-500"
                bgLight="bg-violet-50"
                darkClass="stat-card-violet"
              />
              <StatsCard
                label="Successful Visits"
                value={myVisitorCount.present}
                subtext={`${myVisitorCount.total > 0 ? Math.round((myVisitorCount.present / myVisitorCount.total) * 100) : 0}% success rate`}
                icon={CheckCircle}
                gradient="from-emerald-400 to-green-500"
                bgLight="bg-emerald-50"
                darkClass="stat-card-emerald"
              />
            </div>

            {/* Partner Store Stats */}
            {partnerStoreStats && partnerStoreStats.total_transactions > 0 && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="partner-store-widget">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base md:text-lg font-semibold text-primary flex items-center gap-2">
                    <Storefront className="w-5 h-5 text-bch-gold" weight="duotone" />
                    Partner Store Overview
                  </h2>
                  <a href="/admin/partner-stores" className="text-bch-teal text-xs font-medium hover:underline">View Details</a>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-surface rounded-xl">
                    <p className="text-muted text-[10px] uppercase tracking-wider">Transactions</p>
                    <p className="text-primary text-lg font-bold" data-testid="ps-widget-txns">{partnerStoreStats.total_transactions}</p>
                  </div>
                  <div className="p-3 bg-surface rounded-xl">
                    <p className="text-muted text-[10px] uppercase tracking-wider">Total Spent</p>
                    <p className="text-primary text-lg font-bold" data-testid="ps-widget-spent">{formatNumber(partnerStoreStats.total_spent)}</p>
                  </div>
                  <div className="p-3 bg-surface rounded-xl">
                    <p className="text-muted text-[10px] uppercase tracking-wider">Members Saved</p>
                    <p className="text-emerald-500 text-lg font-bold" data-testid="ps-widget-saved">{formatNumber(partnerStoreStats.total_saved)}</p>
                  </div>
                  <div className="p-3 bg-surface rounded-xl">
                    <p className="text-muted text-[10px] uppercase tracking-wider">Active Stores</p>
                    <p className="text-primary text-lg font-bold" data-testid="ps-widget-stores">{partnerStoreStats.total_stores}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Chapter-wise Performance */}
            {adminData.chapter_stats?.length > 0 && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="chapter-performance">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg md:text-xl font-semibold text-primary flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-bch-gold" weight="fill" />
                    Chapter Performance
                  </h2>
                  <span className="text-muted text-xs">{dateFilter > 0 ? `Last ${dateFilter} days` : "All time"}</span>
                </div>
                <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-[var(--border-color)]">
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider pb-3 pr-4">Chapter</th>
                        <th className="text-left text-muted text-xs font-semibold uppercase tracking-wider pb-3 pr-4">City</th>
                        <th className="text-center text-muted text-xs font-semibold uppercase tracking-wider pb-3 px-2">Members</th>
                        <th className="text-center text-muted text-xs font-semibold uppercase tracking-wider pb-3 px-2">Referrals</th>
                        <th className="text-right text-muted text-xs font-semibold uppercase tracking-wider pb-3 px-2">Business</th>
                        <th className="text-center text-muted text-xs font-semibold uppercase tracking-wider pb-3 pl-2">Meetings</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-color)]">
                      {adminData.chapter_stats.map((ch) => (
                        <tr key={ch.id} className="hover:bg-surface/50 transition-colors">
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shrink-0">
                                <Buildings className="w-4 h-4 text-white" weight="fill" />
                              </div>
                              <span className="text-primary text-sm font-medium">{ch.name}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-secondary text-sm flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 text-rose-400" weight="fill" />
                              {ch.city || "—"}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="inline-flex items-center justify-center bg-violet-50 text-violet-600 text-xs font-semibold px-2.5 py-1 rounded-lg">{ch.members}</span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="inline-flex items-center justify-center bg-emerald-50 text-emerald-600 text-xs font-semibold px-2.5 py-1 rounded-lg">{ch.referrals}</span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <span className="text-amber-600 font-mono text-sm font-semibold">₹{formatNumber(ch.business || 0)}</span>
                          </td>
                          <td className="py-3 pl-2 text-center">
                            <span className="inline-flex items-center justify-center bg-sky-50 text-sky-600 text-xs font-semibold px-2.5 py-1 rounded-lg">{ch.meetings}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Performers Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {/* Top Givers */}
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm">
                <h3 className="text-base font-semibold text-primary mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" weight="fill" />
                  </div>
                  Top Business Givers
                </h3>
                <div className="space-y-2">
                  {(adminData.top_givers || []).slice(0, 5).map((u, i) => (
                    <div key={u.id} className="flex items-center gap-3 p-2.5 bg-surface rounded-xl">
                      <span className="text-muted text-xs font-mono w-5 text-center">{i + 1}</span>
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">{u.full_name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-primary text-sm font-medium truncate">{u.full_name}</p>
                        <p className="text-muted text-xs truncate">{u.business_category}</p>
                      </div>
                      <p className="text-amber-500 font-mono text-sm font-semibold">₹{formatNumber(u.business_given || 0)}</p>
                    </div>
                  ))}
                  {(!adminData.top_givers || adminData.top_givers.length === 0) && (
                    <p className="text-muted text-sm text-center py-4">No data yet</p>
                  )}
                </div>
              </div>

              {/* Top Earners */}
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm">
                <h3 className="text-base font-semibold text-primary mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                    <ChartLineUp className="w-4 h-4 text-white" weight="fill" />
                  </div>
                  Top Business Earners
                </h3>
                <div className="space-y-2">
                  {(adminData.top_earners || []).slice(0, 5).map((u, i) => (
                    <div key={u.id} className="flex items-center gap-3 p-2.5 bg-surface rounded-xl">
                      <span className="text-muted text-xs font-mono w-5 text-center">{i + 1}</span>
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">{u.full_name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-primary text-sm font-medium truncate">{u.full_name}</p>
                        <p className="text-muted text-xs truncate">{u.business_category}</p>
                      </div>
                      <p className="text-emerald-500 font-mono text-sm font-semibold">₹{formatNumber(u.business_gained || 0)}</p>
                    </div>
                  ))}
                  {(!adminData.top_earners || adminData.top_earners.length === 0) && (
                    <p className="text-muted text-sm text-center py-4">No data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Top Visitor Inviters */}
            {topInviters.length > 0 && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="top-inviters">
                <h3 className="text-base font-semibold text-primary mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-white" weight="fill" />
                  </div>
                  Top Visitor Inviters
                </h3>
                <div className="space-y-2">
                  {topInviters.slice(0, 5).map((u, i) => (
                    <div key={u.id} className="flex items-center gap-3 p-2.5 bg-surface rounded-xl">
                      <span className="text-muted text-xs font-mono w-5 text-center">{i + 1}</span>
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">{u.name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-primary text-sm font-medium truncate">{u.name}</p>
                        <p className="text-muted text-xs truncate">{u.chapter_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-pink-500 font-mono text-sm font-semibold">{u.total_visitors}</p>
                        <p className="text-muted text-[10px]">{u.interested_count} interested</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Availability — Super Admin City > Chapter > Categories */}
            {categoryData?.cities && (
              <AdminCategoryAvailability data={categoryData} userName={user?.full_name} />
            )}

            {/* Add Chapter Modal */}
            {showAddChapter && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="add-chapter-modal">
                <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddChapter(false)} />
                <div className="relative bg-card border border-[var(--border-color)] rounded-2xl p-6 w-full max-w-md shadow-2xl">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <Buildings className="w-5 h-5 text-bch-teal" weight="fill" />
                      Create New Chapter
                    </h3>
                    <button onClick={() => setShowAddChapter(false)} className="p-1.5 hover:bg-surface rounded-lg transition-colors">
                      <X className="w-4 h-4 text-muted" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateChapter} className="space-y-4">
                    <div>
                      <label className="text-secondary text-xs font-medium mb-1.5 block">Chapter Name *</label>
                      <input
                        data-testid="chapter-name-input"
                        required
                        value={chapterForm.name}
                        onChange={(e) => setChapterForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Mumbai Central"
                        className="w-full px-4 py-2.5 bg-surface border border-[var(--border-color)] rounded-xl text-primary text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-bch-teal/30 focus:border-bch-teal transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-secondary text-xs font-medium mb-1.5 block">City *</label>
                        <input
                          data-testid="chapter-city-input"
                          required
                          value={chapterForm.city}
                          onChange={(e) => setChapterForm(f => ({ ...f, city: e.target.value }))}
                          placeholder="e.g. Mumbai"
                          className="w-full px-4 py-2.5 bg-surface border border-[var(--border-color)] rounded-xl text-primary text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-bch-teal/30 focus:border-bch-teal transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-secondary text-xs font-medium mb-1.5 block">State</label>
                        <input
                          data-testid="chapter-state-input"
                          value={chapterForm.state}
                          onChange={(e) => setChapterForm(f => ({ ...f, state: e.target.value }))}
                          placeholder="e.g. Maharashtra"
                          className="w-full px-4 py-2.5 bg-surface border border-[var(--border-color)] rounded-xl text-primary text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-bch-teal/30 focus:border-bch-teal transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-secondary text-xs font-medium mb-1.5 block">Description</label>
                      <textarea
                        data-testid="chapter-desc-input"
                        value={chapterForm.description}
                        onChange={(e) => setChapterForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Brief description of the chapter..."
                        rows={3}
                        className="w-full px-4 py-2.5 bg-surface border border-[var(--border-color)] rounded-xl text-primary text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-bch-teal/30 focus:border-bch-teal transition-all resize-none"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAddChapter(false)}
                        className="flex-1 py-2.5 border border-[var(--border-color)] rounded-xl text-secondary hover:bg-surface text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        data-testid="chapter-submit-btn"
                        disabled={creatingChapter}
                        className="flex-1 py-2.5 bg-gradient-to-r from-bch-teal to-emerald-500 text-white rounded-xl text-sm font-medium shadow-md disabled:opacity-50 transition-all hover:shadow-lg"
                      >
                        {creatingChapter ? "Creating..." : "Create Chapter"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Breakdown Modal */}
            {breakdownData && (
              <BreakdownModal data={breakdownData} onClose={() => setBreakdownData(null)} />
            )}
          </>
        ) : (
          <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            <StatsCard
              label="Business Gained"
              value={`₹${formatNumber(stats.business_gained || 0)}`}
              icon={ChartLineUp}
              gradient="from-amber-400 to-orange-500"
              bgLight="bg-amber-50"
              darkClass="stat-card-amber"
            />
            <StatsCard
              label="Business Given"
              value={`₹${formatNumber(stats.business_given || 0)}`}
              icon={ArrowsLeftRight}
              gradient="from-teal-400 to-emerald-500"
              bgLight="bg-emerald-50"
              darkClass="stat-card-emerald"
            />
            <StatsCard
              label="Referrals Given"
              value={stats.referrals_given || 0}
              subtext={`${stats.referrals_received || 0} received`}
              icon={Users}
              gradient="from-violet-400 to-purple-500"
              bgLight="bg-violet-50"
              darkClass="stat-card-violet"
            />
            <StatsCard
              label="Referrals Received"
              value={stats.referrals_received || 0}
              icon={Handshake}
              gradient="from-pink-400 to-rose-500"
              bgLight="bg-pink-50"
              darkClass="stat-card-rose"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <StatsCard
              label="Attendance"
              value={`${stats.attendance_percentage || 0}%`}
              subtext="Chapter meetings"
              icon={CalendarCheck}
              gradient="from-sky-400 to-blue-500"
              bgLight="bg-sky-50"
              darkClass="stat-card-sky"
            />
            <StatsCard
              label="Visitors Brought"
              value={myVisitorCount.present}
              subtext={`${myVisitorCount.total} invited, ${myVisitorCount.total > 0 ? Math.round((myVisitorCount.present / myVisitorCount.total) * 100) : 0}% success`}
              icon={UsersThree}
              gradient="from-violet-400 to-purple-500"
              bgLight="bg-violet-50"
              darkClass="stat-card-violet"
            />
          </div>
          </>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Upcoming Meetings */}
          <div className="lg:col-span-2 bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg md:text-xl font-semibold text-primary">Upcoming Meetings</h2>
              <Link to="/meetings" className="text-bch-teal text-sm hover:text-bch-teal/80 flex items-center gap-1 font-medium">
                View all <CaretRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.upcoming_meetings?.length > 0 ? (
                dashboardData.upcoming_meetings.map((meeting) => (
                  <div key={meeting.id} className="flex items-center justify-between p-4 bg-surface rounded-xl border border-[var(--border-color)] hover:border-bch-teal/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shrink-0">
                        <CalendarCheck className="w-5 h-5 text-white" weight="fill" />
                      </div>
                      <div>
                        <h3 className="text-primary font-medium text-sm md:text-base">{meeting.title}</h3>
                        <p className="text-muted text-xs">{meeting.chapter_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-bch-gold font-mono text-xs md:text-sm font-medium">{meeting.meeting_date}</p>
                      <p className="text-muted text-xs">{meeting.meeting_time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10">
                  <CalendarCheck className="w-12 h-12 text-muted/30 mx-auto mb-3" weight="duotone" />
                  <p className="text-muted text-sm">No upcoming meetings</p>
                </div>
              )}
            </div>
          </div>

          {/* Right column — Performance (Super Admin) or Top Visitor Inviters (Leadership) */}
          {isSuperAdmin ? (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm">
            <h2 className="text-lg md:text-xl font-semibold text-primary mb-5">Performance</h2>
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-secondary">Conversion Rate</span>
                  <span className="text-amber-500 font-mono font-semibold">{stats.conversion_rate || 0}%</span>
                </div>
                <div className="h-2.5 bg-surface rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(stats.conversion_rate || 0, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-secondary">Total Savings</span>
                  <span className="text-emerald-500 font-mono font-semibold">₹{formatNumber(stats.total_savings || 0)}</span>
                </div>
                <Link
                  to="/stores"
                  className="text-bch-gold text-xs hover:text-bch-gold/80 flex items-center gap-1 font-medium"
                  data-testid="view-stores-link"
                >
                  View Partner Stores <CaretRight className="w-3 h-3" />
                </Link>
              </div>
              <Link
                to={`/profile/${user?.id}`}
                className="block w-full text-center py-3 border border-[var(--border-color)] rounded-xl text-secondary hover:bg-surface hover:text-primary transition-all text-sm font-medium"
              >
                View Full Profile
              </Link>
            </div>
          </div>
          ) : topInviters.length > 0 && (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="top-inviters-sidebar">
            <h2 className="text-lg md:text-xl font-semibold text-primary mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" weight="fill" />
              </div>
              Top Visitor Inviters
            </h2>
            <div className="space-y-3">
              {topInviters.slice(0, 6).map((u, i) => (
                <div key={u.id} className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-[var(--border-color)]">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    i === 0 ? "bg-gradient-to-br from-amber-400 to-orange-500" :
                    i === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                    i === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700" :
                    "bg-gradient-to-br from-pink-400 to-rose-500"
                  }`}>
                    <span className="text-white text-xs font-bold">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-primary text-sm font-medium truncate">{u.name}</p>
                    <p className="text-muted text-xs truncate">{u.chapter_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-pink-500 font-semibold text-sm">{u.total_visitors}</p>
                    <p className="text-muted text-[10px]">visitors</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
        {isSuperAdmin && topInviters.length > 0 && (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="top-inviters-all">
            <h2 className="text-lg md:text-xl font-semibold text-primary mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" weight="fill" />
              </div>
              Top Visitor Inviters
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {topInviters.slice(0, 6).map((u, i) => (
                <div key={u.id} className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-[var(--border-color)]">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    i === 0 ? "bg-gradient-to-br from-amber-400 to-orange-500" :
                    i === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                    i === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700" :
                    "bg-gradient-to-br from-pink-400 to-rose-500"
                  }`}>
                    <span className="text-white text-xs font-bold">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-primary text-sm font-medium truncate">{u.name}</p>
                    <p className="text-muted text-xs truncate">{u.chapter_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-pink-500 font-semibold text-sm">{u.total_visitors}</p>
                    <p className="text-muted text-[10px]">visitors</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chapter Category Availability - for non-admin members */}
        {!isSuperAdmin && categoryData?.taken && (
          <MemberCategoryAvailability data={categoryData} userName={user?.full_name} />
        )}

        {/* Recent Activity */}
        <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm">
          <h2 className="text-lg md:text-xl font-semibold text-primary mb-5">Recent Activity</h2>
          <div className="space-y-3">
            {dashboardData?.recent_activity?.length > 0 ? (
              dashboardData.recent_activity.map((item) => (
                <div key={item.id} className="flex items-center gap-3 md:gap-4 p-4 bg-surface rounded-xl border border-[var(--border-color)] hover:border-bch-teal/20 transition-colors">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    item.status === "converted" 
                      ? "bg-gradient-to-br from-emerald-400 to-green-500" 
                      : "bg-gradient-to-br from-teal-400 to-emerald-500"
                  }`}>
                    <ArrowsLeftRight className="w-5 h-5 text-white" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-primary font-medium text-sm md:text-base truncate">{item.client_name}</p>
                    <p className="text-muted text-xs">{item.business_category} • <span className="capitalize">{item.status}</span></p>
                  </div>
                  {item.converted_value && (
                    <p className="text-amber-500 font-mono text-sm md:text-base font-semibold">₹{formatNumber(item.converted_value)}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-10">
                <ArrowsLeftRight className="w-12 h-12 text-muted/30 mx-auto mb-3" weight="duotone" />
                <p className="text-muted text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const MemberCategoryAvailability = ({ data, userName }) => {
  const taken = data.taken || [];
  const available = data.available || [];
  const [inviteModal, setInviteModal] = useState({ open: false, category: "" });
  return (
    <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="category-availability">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg md:text-xl font-semibold text-primary flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
            <Tag className="w-4 h-4 text-white" weight="fill" />
          </div>
          Chapter Categories
        </h2>
        <span className="text-muted text-xs">{data.chapter_name} &middot; {taken.length}/{data.total_categories} filled</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Taken */}
        <div>
          <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3">Taken ({taken.length})</p>
          <div className="space-y-2">
            {taken.map((t) => (
              <div key={t.category} className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-[var(--border-color)]">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-primary text-sm font-medium truncate">{t.category}</p>
                  <p className="text-muted text-xs truncate">{t.member_name} &middot; {t.business_name}</p>
                </div>
                <button
                  onClick={() => setInviteModal({ open: true, category: t.category })}
                  className="shrink-0 p-1.5 rounded-lg text-muted hover:text-bch-teal hover:bg-bch-teal/10 transition-all"
                  title="Generate invite link"
                  data-testid={`invite-taken-${t.category.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <LinkSimple className="w-4 h-4" weight="bold" />
                </button>
              </div>
            ))}
            {taken.length === 0 && <p className="text-muted text-sm py-3 text-center">No categories taken yet</p>}
          </div>
        </div>
        {/* Available */}
        <div>
          <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
            Available ({available.length})
            <span className="text-[10px] font-normal normal-case text-bch-teal">Click to invite!</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {available.map((cat) => (
              <button
                key={cat}
                onClick={() => setInviteModal({ open: true, category: cat })}
                className="flex items-center gap-2.5 p-3 bg-bch-teal/5 border border-bch-teal/15 rounded-xl hover:bg-bch-teal/10 hover:border-bch-teal/30 transition-all cursor-pointer text-left group"
                data-testid={`available-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="w-6 h-6 rounded-md bg-bch-teal/15 flex items-center justify-center shrink-0 group-hover:bg-bch-teal/25 transition-colors">
                  <Plus className="w-3.5 h-3.5 text-bch-teal" weight="bold" />
                </div>
                <span className="text-primary text-sm font-medium truncate">{cat}</span>
              </button>
            ))}
            {available.length === 0 && <p className="text-emerald-600 text-sm py-3 text-center font-medium col-span-2">All categories filled!</p>}
          </div>
        </div>
      </div>
      <InviteLinkModal
        isOpen={inviteModal.open}
        onClose={() => setInviteModal({ open: false, category: "" })}
        category={inviteModal.category}
        chapterName={data.chapter_name}
        chapterId={data.chapter_id}
        inviterName={userName}
      />
    </div>
  );
};

const AdminCategoryAvailability = ({ data, userName }) => {
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [inviteModal, setInviteModal] = useState({ open: false, category: "", chapterName: "", chapterId: "" });

  const cities = data.cities || [];
  const cityChapters = selectedCity ? (cities.find(c => c.city === selectedCity)?.chapters || []) : [];

  return (
    <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 md:p-6 shadow-sm" data-testid="admin-category-availability">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <h2 className="text-lg md:text-xl font-semibold text-primary flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
            <Tag className="w-4 h-4 text-white" weight="fill" />
          </div>
          Category Availability
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedCity}
            onChange={(e) => { setSelectedCity(e.target.value); setSelectedChapter(null); }}
            className="bg-surface border border-[var(--border-color)] rounded-xl px-3 py-2 text-primary text-sm focus:outline-none focus:ring-2 focus:ring-bch-teal/30 cursor-pointer"
            data-testid="admin-cat-city-filter"
          >
            <option value="">Select City</option>
            {cities.map(c => (
              <option key={c.city} value={c.city}>{c.city}</option>
            ))}
          </select>
          {selectedCity && (
            <select
              value={selectedChapter?.chapter_id || ""}
              onChange={(e) => setSelectedChapter(cityChapters.find(ch => ch.chapter_id === e.target.value) || null)}
              className="bg-surface border border-[var(--border-color)] rounded-xl px-3 py-2 text-primary text-sm focus:outline-none focus:ring-2 focus:ring-bch-teal/30 cursor-pointer"
              data-testid="admin-cat-chapter-filter"
            >
              <option value="">Select Chapter</option>
              {cityChapters.map(ch => (
                <option key={ch.chapter_id} value={ch.chapter_id}>
                  {ch.chapter_name} ({ch.taken_count}/{ch.total_categories})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {!selectedCity ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cities.map(cityGroup => (
            cityGroup.chapters.map(ch => (
              <button
                key={ch.chapter_id}
                onClick={() => { setSelectedCity(cityGroup.city); setSelectedChapter(ch); }}
                className="p-4 bg-surface rounded-xl border border-[var(--border-color)] hover:border-bch-teal/30 transition-all text-left group"
                data-testid={`cat-card-${ch.chapter_name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-primary text-sm font-semibold">{ch.chapter_name}</span>
                  <CaretRight className="w-4 h-4 text-muted group-hover:text-bch-teal transition-colors" />
                </div>
                <p className="text-muted text-xs mb-3 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-rose-400" weight="fill" />
                  {cityGroup.city}
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-[var(--bg-main)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-bch-teal to-emerald-500 rounded-full transition-all"
                      style={{ width: `${(ch.taken_count / ch.total_categories) * 100}%` }}
                    />
                  </div>
                  <span className="text-muted text-[11px] font-mono shrink-0">{ch.taken_count}/{ch.total_categories}</span>
                </div>
                <p className="text-bch-teal text-xs mt-2 font-medium">{ch.available_count} available</p>
              </button>
            ))
          ))}
        </div>
      ) : selectedChapter ? (
        <div>
          <button
            onClick={() => setSelectedChapter(null)}
            className="text-bch-teal text-sm mb-4 hover:text-bch-teal/80 flex items-center gap-1 font-medium"
          >
            &larr; Back to {selectedCity} chapters
          </button>
          <div className="flex items-center gap-3 mb-4 p-3 bg-surface rounded-xl border border-[var(--border-color)]">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
              <Buildings className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-primary font-semibold">{selectedChapter.chapter_name}</p>
              <p className="text-muted text-xs">{selectedChapter.taken_count} taken &middot; {selectedChapter.available_count} available of {selectedChapter.total_categories}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3">Taken ({selectedChapter.taken_count})</p>
              <div className="space-y-2">
                {selectedChapter.taken.map(t => (
                  <div key={t.category} className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-[var(--border-color)]">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-primary text-sm font-medium truncate">{t.category}</p>
                      <p className="text-muted text-xs truncate">{t.member_name} &middot; {t.business_name}</p>
                    </div>
                  </div>
                ))}
                {selectedChapter.taken.length === 0 && <p className="text-muted text-sm py-3 text-center">None</p>}
              </div>
            </div>
            <div>
              <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                Available ({selectedChapter.available_count})
                <span className="text-[10px] font-normal normal-case text-bch-teal">Click to invite!</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedChapter.available.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setInviteModal({ open: true, category: cat, chapterName: selectedChapter.chapter_name, chapterId: selectedChapter.chapter_id })}
                    className="flex items-center gap-2.5 p-3 bg-bch-teal/5 border border-bch-teal/15 rounded-xl hover:bg-bch-teal/10 hover:border-bch-teal/30 transition-all cursor-pointer text-left group"
                  >
                    <div className="w-6 h-6 rounded-md bg-bch-teal/15 flex items-center justify-center shrink-0 group-hover:bg-bch-teal/25 transition-colors">
                      <Plus className="w-3.5 h-3.5 text-bch-teal" weight="bold" />
                    </div>
                    <span className="text-primary text-sm font-medium truncate">{cat}</span>
                  </button>
                ))}
                {selectedChapter.available.length === 0 && <p className="text-emerald-600 text-sm py-3 text-center font-medium col-span-2">All filled!</p>}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => { setSelectedCity(""); setSelectedChapter(null); }}
            className="text-bch-teal text-sm mb-4 hover:text-bch-teal/80 flex items-center gap-1 font-medium"
          >
            &larr; All cities
          </button>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cityChapters.map(ch => (
              <button
                key={ch.chapter_id}
                onClick={() => setSelectedChapter(ch)}
                className="p-4 bg-surface rounded-xl border border-[var(--border-color)] hover:border-bch-teal/30 transition-all text-left"
              >
                <p className="text-primary text-sm font-semibold mb-2">{ch.chapter_name}</p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-2 bg-[var(--bg-main)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-bch-teal to-emerald-500 rounded-full"
                      style={{ width: `${(ch.taken_count / ch.total_categories) * 100}%` }}
                    />
                  </div>
                  <span className="text-muted text-[11px] font-mono shrink-0">{ch.taken_count}/{ch.total_categories}</span>
                </div>
                <p className="text-bch-teal text-xs font-medium">{ch.available_count} available</p>
              </button>
            ))}
          </div>
        </div>
      )}
      <InviteLinkModal
        isOpen={inviteModal.open}
        onClose={() => setInviteModal({ open: false, category: "", chapterName: "", chapterId: "" })}
        category={inviteModal.category}
        chapterName={inviteModal.chapterName}
        chapterId={inviteModal.chapterId}
        inviterName={userName}
      />
    </div>
  );
};

const StatsCard = ({ label, value, subtext, icon: Icon, gradient, bgLight, darkClass, onClick }) => (
  <div 
    className={`${bgLight} ${darkClass || ""} border border-[var(--border-color)] rounded-2xl p-4 md:p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${onClick ? "cursor-pointer" : ""}`}
    onClick={onClick}
    data-testid={`stats-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-secondary text-xs md:text-sm mb-2 truncate">{label}</p>
        <p className="font-mono text-xl md:text-2xl font-bold text-primary">
          {value}
        </p>
        {subtext && <p className="text-muted text-xs mt-1 hidden sm:block">{subtext}</p>}
      </div>
      <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-5 h-5 md:w-5.5 md:h-5.5 text-white" weight="fill" />
      </div>
    </div>
  </div>
);

const formatNumber = (num) => {
  if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
  if (num >= 100000) return (num / 100000).toFixed(1) + "L";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString("en-IN");
};

const BREAKDOWN_TABS = [
  { key: "city_breakdown", label: "City-wise", icon: MapPin },
  { key: "chapter_breakdown", label: "Chapter-wise", icon: Buildings },
  { key: "member_breakdown", label: "Member-wise", icon: Users },
];

const BreakdownModal = ({ data, onClose }) => {
  const [tab, setTab] = useState("city_breakdown");
  if (!data) return null;

  const items = data[tab] || [];
  const fmt = data.format;

  const renderValue = (item) => {
    if (fmt === "currency") return <span className="text-amber-600 font-mono text-sm font-semibold">₹{formatNumber(item.value)}</span>;
    if (fmt === "members") return <span className="text-muted text-xs">{item.value}</span>;
    if (fmt === "pending") return <span className="text-muted text-xs truncate">{item.value}</span>;
    if (fmt === "chapters") return (
      <span className="text-sm">
        <span className="text-violet-600 font-semibold">{item.value}</span>
        <span className="text-muted text-xs ml-1">members</span>
        {item.city && <span className="text-muted text-xs ml-2">• {item.city}</span>}
      </span>
    );
    return <span className="font-mono text-sm font-semibold text-primary">{item.value}</span>;
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="breakdown-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-[var(--border-color)] rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-[var(--border-color)] flex items-center justify-between">
          <h3 className="text-lg font-semibold text-primary">{data.metric} Breakdown</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-surface rounded-lg transition-colors" data-testid="breakdown-close">
            <X className="w-4 h-4 text-muted" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border-color)]">
          {BREAKDOWN_TABS.map((t) => {
            const TabIcon = t.icon;
            const count = (data[t.key] || []).length;
            if (count === 0 && t.key !== tab) return null;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`breakdown-tab-${t.key}`}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2 ${
                  tab === t.key
                    ? "text-bch-teal border-bch-teal"
                    : "text-muted border-transparent hover:text-primary"
                }`}
              >
                <TabIcon className="w-4 h-4" weight={tab === t.key ? "fill" : "duotone"} />
                {t.label}
                {count > 0 && <span className="text-xs bg-surface px-1.5 py-0.5 rounded-md">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="max-h-[28rem] overflow-y-auto p-5">
          {items.length > 0 ? (
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-surface rounded-xl border border-[var(--border-color)]">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-muted text-xs font-mono w-5 text-center shrink-0">{i + 1}</span>
                    <p className="text-primary text-sm font-medium truncate">{item.name || "—"}</p>
                  </div>
                  {renderValue(item)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-muted text-sm">No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
